/* eslint-disable @typescript-eslint/ban-types */
// this eslint exclusion is needed in order to be able to use 'Function' as a parameter in Injectable

import { ErrorMessagesRecord } from "../services/errorMessage.service";

export const INJECTOR_DEPENDENCY_INJECTION_NAMES_VAR = "Injector_dependencyInjectionNames";
export const INJECTOR_GET_ERROR_MESSAGES_FUNCTION_NAME_VAR = "ErrorMessageService_getServiceErrorMessagesFunctionName";

/**
 * Decorator that marks a class as injectable as a dependency.
 * 
 * Usage :
 * 
 * export interface AppService ...
 * 
 * @ Injectable("AppService")
 * export class ClientAppService ...
 * 
 * @param interfaceNames the provided string (or string array if class provides several services)
 * interfaceName can be anything but is usually the interface name of a service.
 * @param options
 * @param options.registerOnErrorMessageService true => service implements ServiceWithErrorMessages / functionName : service provides errorMessages with this function
 * when registerOnErrorMessageService is set, the errorMessages provided by the service getServiceErrorMessages will be registered on the ErrorMessageService
 * @param options.onServiceRegister true => onServiceRegister will be called on register / function : function will be called on register
 */
export function Injectable(interfaceNames: string | string[], options?: {
    registerOnErrorMessageService?: true | string,
    onServiceRegister?: true | string
}): (constructorFunction: Function) => void {
    return (constructorFunction: Function): void => {
        // record Dependency Name on prototype for use by injector
        constructorFunction.prototype[INJECTOR_DEPENDENCY_INJECTION_NAMES_VAR] = interfaceNames;

        // Record getServiceErrorMessages function name on prototype for use by injector
        const getServiceErrorMessagesFuncName = options?.registerOnErrorMessageService === true ? "getServiceErrorMessages" : options?.registerOnErrorMessageService;
        if (getServiceErrorMessagesFuncName != null) {
            constructorFunction.prototype.ErrorMessageService_getServiceErrorMessagesFunctionName = getServiceErrorMessagesFuncName;
        }

        // Record onServiceRegister
        const onRegisterFuncName = options?.onServiceRegister === true ? "onServiceRegister" : options?.onServiceRegister;
        if (onRegisterFuncName != null) {
            constructorFunction.prototype.Injector_onServiceRegister = onRegisterFuncName;
        }
    };
}

/**
 * ServiceWithErrorMessages should be decorated with @Injectable("ServiceName", { registerOnErrorMessagesService : true })
 */
export interface ServiceWithErrorMessages<Lang extends string = string> {

    /**
     * This function is called by injector when a ServiceWithErrorMessages is registered
     * 
     * If the ErrorMessageService is registered later, the injector will have kept the concerned services 
     * and will register the error messages on ErrorMessageService registration
     * 
     * @returns a record with error messages by lang and code {[lang] : {[errorCode] : errorMessage}}
     */
    getServiceErrorMessages(): ErrorMessagesRecord<Lang>;
}

export { ErrorMessagesRecord } from "../services/errorMessage.service";
